import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { REWARD_PERIOD_STATUSES, DATE_FORMAT, DEFAULT_DISPLAY_VALUE, PAGE_NAMES } from '@/constants';
import { convertNumberToFourDigitsString, formatDateToLocalDateTime } from '@/helpers';
import { DisplayStatus, RewardPeriodDTO, TableHeaderColumns, UserDTO } from '@/models';
import { createNewRewardPeriod, fetchRewardPeriods } from '@/services';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import Status from '@/components/common/status/Status.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import RewardPeriodForm from '@/components/pages/reward-management/reward-period/reward-period-form/RewardPeriodForm.vue';
import RewardPeriodExport from '@/components/pages/reward-management/reward-period/reward-period-export/RewardPeriodExport.vue';

@Component({
  components: {
    SearchInput,
    InlineLoader,
    Pagination,
    Status,
    RewardPeriodForm,
    RewardPeriodExport,
    UserInformation
  }
})
export default class RewardPeriod extends Vue {
  private currentUser: UserDTO = {} as UserDTO;
  private isFetchingRewardPeriod = true;
  private isCreateNew = false;
  private headers: TableHeaderColumns = [
    {
      text: 'ID',
      align: 'start',
      value: 'id'
    },
    { text: 'Reward Period Name', value: 'name' },
    { text: 'Status', value: 'status' },
    {
      text: 'Description',
      value: 'description',
      cellClass: 'reward-period__cell-description'
    },
    { text: 'Start date', value: 'startDate' },
    { text: 'End date', value: 'endDate' },
    { text: 'Created by', value: 'createdBy', align: 'start' },
    { text: '', value: 'detail', align: 'end', sortable: false }
  ];
  private rewardPeriods: RewardPeriodDTO[] = [];
  private search = '';
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;

  openCreateNewDialog(): void {
    this.isCreateNew = true;
  }

  closeCreateNewDialog(): void {
    this.isCreateNew = false;
  }

  displayID(id: number): string {
    return convertNumberToFourDigitsString(id);
  }

  displayDate(date: string): string {
    if (date) {
      return moment(date).format(DATE_FORMAT.DISPLAY);
    }
    return DEFAULT_DISPLAY_VALUE.DATE;
  }

  hasOnGoingPeriod(): boolean {
    if (this.rewardPeriods.length !== 0) {
      const checkPeriod = this.rewardPeriods.some(item => item.status === REWARD_PERIOD_STATUSES.PUBLISHING.label);
      if (checkPeriod) {
        return true;
      }
    }
    return false;
  }

  filterByNameAndId(value: keyof RewardPeriodDTO, search: string, item: RewardPeriodDTO): boolean {
    return (
      value !== null &&
      search !== null &&
      (convertNumberToFourDigitsString(item.id || -1).includes(search) || item.name.includes(search))
    );
  }

  created(): void {
    this.currentUser = this.$store.getters['app/currentUser'];
  }

  mounted(): void {
    this.isFetchingRewardPeriod = false;
    fetchRewardPeriods()
      .then(payload => {
        this.rewardPeriods = payload.data || [];
      })
      .finally(() => {
        this.isFetchingRewardPeriod = false;
      });
  }

  async createNewPeriod(
    name: string,
    description: string,
    startDate: string,
    endDate: string,
    settings: Record<string, number> = {}
  ): Promise<void> {
    const { data } = await createNewRewardPeriod({
      name: name,
      description: description,
      startDate: formatDateToLocalDateTime(startDate),
      endDate: formatDateToLocalDateTime(endDate),
      createdById: this.currentUser.id,
      settings
    });
    if (data) {
      this.rewardPeriods.push(data);
    }
    this.isCreateNew = false;
  }

  async navigateToRewardPeriodDetail(id: number): Promise<void> {
    await this.$router.push({
      name: PAGE_NAMES.REWARD_PERIOD_DETAIL,
      params: {
        id: '' + id
      }
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onSearch(searchText: string): void {
    this.search = searchText;
  }

  getDisplayStatus(status: string): DisplayStatus {
    return REWARD_PERIOD_STATUSES[status];
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }
}
